import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import './ToolPage.css'
import './BingoRoom.css'
import {
  joinBingoRoom,
  fetchBingoCard,
  fetchBingoState,
  bingoStreamUrl,
} from './utils/bingoApiClient'
import { trackEvent } from '../../lib/analytics'
import { useDocumentMeta } from '../../hooks/useDocumentMeta'

const storageKey = (roomId) => `bingo-room-${roomId}-participant`

const BingoRoomJoin = () => {
  useDocumentMeta({
    title: 'Bingo ao Vivo | jarder.dev',
    description: 'Entre na sala de bingo e acompanhe sua cartela ao vivo.',
  })

  const { id } = useParams()
  const [participantId, setParticipantId] = useState(() => localStorage.getItem(storageKey(id)))
  const [name, setName] = useState('')
  const [card, setCard] = useState(null)
  const [drawnNumbers, setDrawnNumbers] = useState([])
  const [won, setWon] = useState(false)
  const [status, setStatus] = useState('checking')
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false

    fetchBingoState(id)
      .then((data) => {
        if (cancelled) return
        setDrawnNumbers(data.drawnNumbers)
        if (participantId) {
          const mine = data.participants.find((p) => p.id === participantId)
          if (mine) setWon(mine.won)
        } else {
          setStatus('needsName')
        }
      })
      .catch(() => {
        if (!cancelled) setStatus('notfound')
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  useEffect(() => {
    if (!participantId || status === 'notfound') return undefined
    let cancelled = false

    fetchBingoCard(id, participantId)
      .then((data) => {
        if (cancelled) return
        setCard(data.card)
        setStatus('ready')
      })
      .catch(() => {
        if (!cancelled) {
          localStorage.removeItem(storageKey(id))
          setStatus('removed')
        }
      })

    return () => {
      cancelled = true
    }
  }, [id, participantId, status])

  useEffect(() => {
    if (status !== 'ready') return undefined
    const eventSource = new EventSource(bingoStreamUrl(id))
    eventSource.onmessage = (event) => {
      const data = JSON.parse(event.data)
      setDrawnNumbers(data.drawnNumbers)

      const mine = data.participants.find((p) => p.id === participantId)
      if (!mine) {
        localStorage.removeItem(storageKey(id))
        setStatus('removed')
        return
      }
      setWon(mine.won)
    }
    return () => eventSource.close()
  }, [id, status, participantId])

  const join = async () => {
    const trimmed = name.trim()
    if (!trimmed) {
      setError('Digite seu nome.')
      return
    }
    setError('')
    try {
      const result = await joinBingoRoom(id, trimmed)
      localStorage.setItem(storageKey(id), result.participantId)
      setParticipantId(result.participantId)
      setCard(result.card)
      setStatus('ready')
      trackEvent('tool_generate', { tool: 'bingo_room_join' })
    } catch {
      setError('Não foi possível entrar na sala. Tente novamente.')
    }
  }

  if (status === 'checking') {
    return (
      <div className="tool-page">
        <p>Carregando...</p>
      </div>
    )
  }

  if (status === 'notfound') {
    return (
      <div className="tool-page">
        <Link to="/ferramentas" className="tool-back-link">
          ← Todas as ferramentas
        </Link>
        <h1>Sala não encontrada</h1>
        <p className="tool-disclaimer">Essa sala não existe ou expirou.</p>
      </div>
    )
  }

  if (status === 'removed') {
    return (
      <div className="tool-page">
        <Link to="/ferramentas" className="tool-back-link">
          ← Todas as ferramentas
        </Link>
        <h1>Você foi removido da sala</h1>
        <p className="tool-disclaimer">
          O mestre da sala removeu você da partida. Recarregue a página
          pra entrar de novo com uma cartela nova.
        </p>
        <button type="button" className="tool-button" onClick={() => window.location.reload()}>
          Recarregar
        </button>
      </div>
    )
  }

  if (status === 'needsName') {
    return (
      <div className="tool-page">
        <Link to="/ferramentas" className="tool-back-link">
          ← Todas as ferramentas
        </Link>
        <h1>Entrar na Sala de Bingo</h1>
        <p className="tool-disclaimer">
          Digite seu nome pra receber sua cartela, ela é gerada na hora,
          uma diferente pra cada pessoa.
        </p>

        <div className="tool-field">
          <label htmlFor="participant-name">Seu nome</label>
          <input
            id="participant-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && join()}
          />
        </div>

        {error && <p className="tool-error">{error}</p>}

        <button type="button" className="tool-button" onClick={join}>
          Entrar
        </button>
      </div>
    )
  }

  const drawnSet = new Set(drawnNumbers)

  return (
    <div className="tool-page">
      <Link to="/ferramentas" className="tool-back-link">
        ← Todas as ferramentas
      </Link>
      <h1>Sua Cartela</h1>
      <p className="tool-disclaimer">
        Sua cartela é marcada automaticamente conforme os números vão
        sendo sorteados. Você não precisa fazer nada.
      </p>

      {won && (
        <p className="bingo-win-banner">
          <span role="img" aria-label="comemoração">
            🎉
          </span>{' '}
          Bingo! Você completou sua cartela.
        </p>
      )}

      <div className={`bingo-card${won ? ' bingo-card-won' : ''}`}>
        <div className="bingo-card-header">
          {['B', 'I', 'N', 'G', 'O'].map((letter) => (
            <span key={letter}>{letter}</span>
          ))}
        </div>
        {card.map((row, rowIndex) => (
          <div className="bingo-card-row" key={rowIndex}>
            {row.map((cell, cellIndex) => {
              const marked = cell === 'FREE' || drawnSet.has(cell)
              return (
                <span
                  key={cellIndex}
                  className={`bingo-card-cell${marked ? ' marked' : ''}`}
                >
                  {cell === 'FREE' ? '★' : cell}
                </span>
              )
            })}
          </div>
        ))}
      </div>

      <h2 className="bingo-drawn-heading">
        Números sorteados ({drawnNumbers.length})
      </h2>
      {drawnNumbers.length === 0 ? (
        <p className="tool-import-hint">Ainda não saiu nenhum número.</p>
      ) : (
        <div className="bingo-drawn-list">
          {[...drawnNumbers]
            .sort((a, b) => b - a)
            .map((number) => (
              <span key={number} className="bingo-drawn-chip">
                {number}
              </span>
            ))}
        </div>
      )}
    </div>
  )
}

export default BingoRoomJoin
